/**
 * Keyframe data for the unlock celebration (GLOSSARY.md) — the one-shot
 * confetti a Classic card plays after its tree unlock is acknowledged.
 * Values are verbatim from the Figma motion data (Shop 2.0, confetti
 * animation inside node 657:22889): a 2s timeline in the component's own
 * 120×96 coordinate space, which the overlay scales up to the mounted
 * 206×164.8 footprint. Each node is a set of motion tracks (`MotionTrack`),
 * one per animated property.
 */

import dot1291 from '~/assets/confetti/dot-1291.svg'
import dot1292 from '~/assets/confetti/dot-1292.svg'
import dot1293 from '~/assets/confetti/dot-1293.svg'
import dot1294 from '~/assets/confetti/dot-1294.svg'
import dot1295 from '~/assets/confetti/dot-1295.svg'
import dot1296 from '~/assets/confetti/dot-1296.svg'
import dot1297 from '~/assets/confetti/dot-1297.svg'
import dot1298 from '~/assets/confetti/dot-1298.svg'
import dot1300 from '~/assets/confetti/dot-1300.svg'
import glow from '~/assets/confetti/glow.svg'
import starLarge from '~/assets/confetti/star-large.svg'
import starSmall from '~/assets/confetti/star-small.svg'
import { trackKeyframes, type MotionTrack } from './motion'

export const CELEBRATION_DURATION_MS = 2000

export interface CelebrationParticle {
  asset: string
  /** Layout box in the 120×96 stage space (px). */
  box: { left: number; top: number; width: number; height: number }
  /** The stars' vector sits inset from the node box (Figma path padding). */
  artInset?: string
  tracks: MotionTrack[]
}

// cubic-bezier(0.5, 0, 0.5, 1) — the curve Figma emits for most segments.
const SYMMETRIC = 'cubic-bezier(0.5, 0, 0.5, 1)'
const EASE_IN_OUT = 'cubic-bezier(0.42, 0, 0.58, 1)'

// The glow's scale rises on a spring Figma expresses as
// 1 - e^(-7.6657t)(cos(6.7605t) + 1.1339·sin(6.7605t)). CSS/WAAPI has no
// spring primitive, so the curve is sampled into linear segments over its
// share of the timeline.
const GLOW_SPRING_SHARE = 0.3975
const GLOW_SPRING_SAMPLES = 24

function springProgress(t: number): number {
  return (
    1 -
    Math.exp(-t * 7.6657) *
      (Math.cos(t * 6.7605) + 1.1339 * Math.sin(t * 6.7605))
  )
}

function glowScaleTrack(): MotionTrack {
  const values: number[] = []
  const times: number[] = []
  for (let i = 0; i < GLOW_SPRING_SAMPLES; i++) {
    const t = i / GLOW_SPRING_SAMPLES
    values.push(springProgress(t))
    times.push(GLOW_SPRING_SHARE * t)
  }
  values.push(1, 1)
  times.push(GLOW_SPRING_SHARE, 1)
  return { property: 'scale', values, times, easing: 'linear' }
}

export const CELEBRATION_PARTICLES: CelebrationParticle[] = [
  {
    // Ellipse 1299 — the central glow burst.
    asset: glow,
    box: { left: 10, top: 15, width: 100, height: 101 },
    tracks: [
      {
        property: 'opacity',
        values: [
          0, 0.10008, 0.56798, 0.78514, 0.93772, 1, 1, 0.95272, 0.77163,
          0.44556, 0.15494, 0.02104, 0, 0,
        ],
        times: [
          0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8,
          1,
        ],
        easing: 'linear',
      },
      glowScaleTrack(),
    ],
  },
  {
    // Star 2 — the small green star; rotation carries its 165° base pose.
    asset: starSmall,
    box: { left: 39.31, top: 18.85, width: 6.152, height: 6.38 },
    artInset: '14.11% 13.27%',
    tracks: [
      {
        property: 'rotate',
        values: ['165.022deg', '165.022deg', '525.022deg', '525.022deg'],
        times: [0, 0.0654, 0.4756, 1],
        easing: ['linear', EASE_IN_OUT, 'linear'],
      },
      {
        property: 'scale',
        values: [0, 1, 1, 0, 0],
        times: [0, 0.0901, 0.3668, 0.7134, 1],
        easing: [SYMMETRIC, 'linear', SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: ['21px 23px', '21px 23px', '0px 0px', '0px 0px'],
        times: [0, 0.3769, 0.377, 1],
        easing: 'linear',
      },
    ],
  },
  {
    // Ellipse 1291
    asset: dot1291,
    box: { left: 24, top: 79, width: 2.717, height: 2.717 },
    tracks: [
      {
        property: 'scale',
        values: [1, 1, 0, 0],
        times: [0, 0.36, 0.6464, 1],
        easing: ['linear', SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: ['33px -16px', '0px 0px', '0px 0px'],
        times: [0, 0.34, 1],
        easing: [EASE_IN_OUT, 'linear'],
      },
    ],
  },
  {
    // Ellipse 1294
    asset: dot1294,
    box: { left: 21, top: 64, width: 2.038, height: 2.038 },
    tracks: [
      {
        property: 'scale',
        values: [0.6, 1, 1, 0, 0],
        times: [0, 0.0654, 0.3207, 0.6346, 1],
        easing: [SYMMETRIC, 'linear', SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: ['31px -4px', '0px 0px', '0px 0px'],
        times: [0, 0.2613, 1],
        easing: [EASE_IN_OUT, 'linear'],
      },
    ],
  },
  {
    // Ellipse 1296
    asset: dot1296,
    box: { left: 15, top: 59, width: 3.396, height: 3.396 },
    tracks: [
      {
        property: 'scale',
        values: [1, 1, 0, 0],
        times: [0, 0.352, 0.615, 1],
        easing: ['linear', SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: [
          '35.619px -6.926px',
          '35.619px -6.926px',
          '0px 0px',
          '0px 0px',
        ],
        times: [0, 0.4412, 0.4413, 1],
        easing: 'linear',
      },
    ],
  },
  {
    // Star 1 — the large yellow star; rotation carries its 18° base pose.
    asset: starLarge,
    box: { left: 80.98, top: 19.69, width: 12.924, height: 13.403 },
    artInset: '14.11% 13.27%',
    tracks: [
      {
        property: 'rotate',
        values: ['18.267deg', '-411.733deg', '-411.733deg'],
        times: [0, 0.501, 1],
        easing: [SYMMETRIC, 'linear'],
      },
      {
        property: 'scale',
        values: [0, 1, 0, 0],
        times: [0, 0.1676, 0.7757, 1],
        easing: [SYMMETRIC, SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: ['-15px 15px', '0px 0px', '0px 0px'],
        times: [0, 0.2743, 1],
        easing: [EASE_IN_OUT, 'linear'],
      },
    ],
  },
  {
    // Ellipse 1297 — a plain circle, so its Figma flip survives only as the
    // animated scaleY sign and its static rotation is dropped.
    asset: dot1297,
    box: { left: 30.09, top: 24.35, width: 2, height: 2 },
    tracks: [
      {
        property: 'scale',
        values: ['0.8 -0.8', '1 -1', '0 0', '0 0'],
        times: [0, 0.2835, 0.5423, 1],
        easing: [SYMMETRIC, SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: ['20px 15px', '0px 0px', '0px 0px'],
        times: [0, 0.2835, 1],
        easing: [EASE_IN_OUT, 'linear'],
      },
    ],
  },
  {
    // Ellipse 1292
    asset: dot1292,
    box: { left: 93, top: 69, width: 3, height: 3 },
    tracks: [
      {
        property: 'scale',
        values: [1, 1, 0, 0],
        times: [0, 0.3287, 0.7134, 1],
        easing: ['linear', SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: ['-30px -7px', '0px 0px', '0px 0px'],
        times: [0, 0.3675, 1],
        easing: [EASE_IN_OUT, 'linear'],
      },
    ],
  },
  {
    // Ellipse 1298
    asset: dot1298,
    box: { left: 31.5, top: 71.5, width: 2, height: 2 },
    tracks: [
      {
        property: 'scale',
        values: [1, 1, 0, 0],
        times: [0, 0.37, 0.6641, 1],
        easing: ['linear', SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: ['25.5px -11.5px', '25.5px -11.5px', '0px 0px', '0px 0px'],
        times: [0, 0.054, 0.3111, 1],
        easing: ['linear', EASE_IN_OUT, 'linear'],
      },
    ],
  },
  {
    // Ellipse 1300
    asset: dot1300,
    box: { left: 85.5, top: 61.5, width: 2, height: 2 },
    tracks: [
      {
        property: 'scale',
        values: [1, 1, 0, 0],
        times: [0, 0.319, 0.6405, 1],
        easing: ['linear', SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: ['-26.5px -2.5px', '0px 0px', '0px 0px'],
        times: [0, 0.287, 1],
        easing: [EASE_IN_OUT, 'linear'],
      },
    ],
  },
  {
    // Ellipse 1293
    asset: dot1293,
    box: { left: 96, top: 46, width: 2.717, height: 2.717 },
    tracks: [
      {
        property: 'scale',
        values: [1, 1, 0, 0],
        times: [0, 0.262, 0.615, 1],
        easing: ['linear', SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: ['-36px 6px', '0px 0px', '0px 0px'],
        times: [0, 0.2645, 1],
        easing: [EASE_IN_OUT, 'linear'],
      },
    ],
  },
  {
    // Ellipse 1295
    asset: dot1295,
    box: { left: 95, top: 42, width: 1, height: 1 },
    tracks: [
      {
        property: 'scale',
        values: [1, 1, 0, 0],
        times: [0, 0.355, 0.6025, 1],
        easing: ['linear', SYMMETRIC, 'linear'],
      },
      {
        property: 'translate',
        values: ['-30px 9px', '0px 0px', '0px 0px'],
        times: [0, 0.258, 1],
        easing: [EASE_IN_OUT, 'linear'],
      },
    ],
  },
]

// Particles rest invisible (base opacity 0) so a finished or never-started
// animation leaves nothing behind; tracks without their own opacity get a
// constant-1 gate for the animation's lifetime.
const VISIBLE_TRACK: MotionTrack = {
  property: 'opacity',
  values: [1, 1],
  times: [0, 1],
  easing: 'linear',
}

/** One WAAPI keyframe list per track, ready for `element.animate`. */
export function particleAnimations(
  particle: CelebrationParticle,
): Keyframe[][] {
  const tracks = particle.tracks.some((track) => track.property === 'opacity')
    ? particle.tracks
    : [VISIBLE_TRACK, ...particle.tracks]
  return tracks.map(trackKeyframes)
}
